/**
 * Status chip for the admin "AI Quality & Observability" page. A thin, named wrapper
 * over the shared StatusBadge so every observability source reads with the same four
 * states and colors:
 *
 *   healthy       → green   (source is live and looks good)
 *   warning       → amber   (live but degraded / needs a look)
 *   missing       → red     (expected data/source is absent)
 *   operator-only → neutral (real but only produced by a manual/operator run)
 *
 * Keeping the mapping in one place stops each section from re-deciding chip colors.
 */

import React from 'react';
import { StatusBadge } from '@/components/ui';
import type { LucideIcon } from 'lucide-react-native';

export type ObservabilityStatus = 'healthy' | 'warning' | 'missing' | 'operator-only';

const STATUS_MAP: Record<ObservabilityStatus, { variant: 'success' | 'warning' | 'error' | 'neutral'; defaultLabel: string }> = {
  healthy: { variant: 'success', defaultLabel: 'Healthy' },
  warning: { variant: 'warning', defaultLabel: 'Warning' },
  missing: { variant: 'error', defaultLabel: 'Missing' },
  'operator-only': { variant: 'neutral', defaultLabel: 'Operator-only' },
};

interface ObservabilityStatusChipProps {
  status: ObservabilityStatus;
  /** Overrides the default label ("Healthy" / "Warning" / …). */
  label?: string;
  icon?: LucideIcon;
  size?: 'sm' | 'md';
}

export function ObservabilityStatusChip({ status, label, icon, size = 'sm' }: ObservabilityStatusChipProps) {
  const { variant, defaultLabel } = STATUS_MAP[status];
  return <StatusBadge label={label ?? defaultLabel} variant={variant} icon={icon} size={size} />;
}
